import { useEffect, useRef, useState } from "react";
import {
  PencilIcon,
  TrashIcon,
  DocumentTextIcon,
} from "@heroicons/react/outline";
import Toast from "../../../../components/toast";
import ToastContainer from "../../../../components/toastContainer";
import SettlementEditModal from "./SettlementFormModal";
import {
  cancelSettlement,
  createManualSettlement,
  getSettlementsByEvent,
  markPaidSettlement,
  updateSettlementSetting,
} from "../../../../api/settlements";
import PageLoader from "../../../../components/pageLoader";
import { CURRENCY_MAP } from "../../../../constants";

// ── Helpers ──
const formatCurrency = (amount, currency) => {
  const symbol = CURRENCY_MAP[currency] || currency;
  return `${symbol} ${amount}`;
};

const statusStyle = (status) => {
  switch (status) {
    case "completed":
      return "text-emerald-400 bg-emerald-500/10 border-emerald-500/20";
    case "in_progress":
      return "text-amber-400 bg-amber-500/10 border-amber-500/20";
    case "failed":
      return "text-red-400 bg-red-500/10 border-red-500/20";
    default:
      return "text-slate-400 bg-slate-500/10 border-slate-500/20";
  }
};

const statusLabel = (status) => {
  switch (status) {
    case "completed":
      return "Completed";
    case "in_progress":
      return "In Progress";
    case "failed":
      return "Failed";
    default:
      return status;
  }
};

// ── Info Row ──
const InfoRow = ({ label, value }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs text-slate-500">{label}</span>
    <span className="text-sm text-slate-200">{value || "—"}</span>
  </div>
);

// ── Field ──
const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="block text-xs text-slate-400">{label}</label>
    {children}
  </div>
);

// ── Company Info Modal ──
const CompanyInfoModal = ({ data, eventId, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    company_name: data?.company_name || "",
    vat_no: data?.vat_no || "",
    representative_name: data?.representative_name || "",
    address_1: data?.address?.address_1 || "",
    address_2: data?.address?.address_2 || "",
    address_3: data?.address?.address_3 || "",
    city: data?.address?.city || "",
    county: data?.address?.county || "",
    country: data?.address?.country || "",
    postcode: data?.address?.postcode || "",
  });

  const handleSubmit = async () => {
    try {
      setSaving(true);
      const payload = {
        company_name: form.company_name,
        vat_no: form.vat_no,
        representative_name: form.representative_name,
        address: {
          address_1: form.address_1,
          address_2: form.address_2,
          address_3: form.address_3,
          city: form.city,
          county: form.county,
          country: form.country,
          postcode: form.postcode,
        },
      };
      const updated = await updateSettlementSetting(eventId, payload);
      onSave(updated);
      onClose();
    } catch (err) {
      console.error("Failed to update company info:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-md bg-slate-900 border border-white/10 rounded-xl shadow-xl">
        <div className="px-6 py-4 border-b border-white/10">
          <h2 className="text-white font-semibold text-sm">
            Edit Company Info
          </h2>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <Field label="Company Name">
            <input
              type="text"
              value={form.company_name}
              onChange={(e) =>
                setForm((p) => ({ ...p, company_name: e.target.value }))
              }
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            />
          </Field>

          <Field label="VAT No">
            <input
              type="text"
              value={form.vat_no}
              onChange={(e) =>
                setForm((p) => ({ ...p, vat_no: e.target.value }))
              }
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            />
          </Field>

          <Field label="Representative Name">
            <input
              type="text"
              value={form.representative_name}
              onChange={(e) =>
                setForm((p) => ({ ...p, representative_name: e.target.value }))
              }
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            />
          </Field>

          <div className="border-t border-white/5 pt-4">
            <p className="text-xs text-slate-500 uppercase tracking-widest mb-3">
              Address
            </p>
            <div className="space-y-3">
              <Field label="Address Line 1">
                <input
                  type="text"
                  value={form.address_1}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, address_1: e.target.value }))
                  }
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                />
              </Field>

              <Field label="Address Line 2">
                <input
                  type="text"
                  value={form.address_2}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, address_2: e.target.value }))
                  }
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                />
              </Field>

              <Field label="Address Line 3">
                <input
                  type="text"
                  value={form.address_3}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, address_3: e.target.value }))
                  }
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="City">
                  <input
                    type="text"
                    value={form.city}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, city: e.target.value }))
                    }
                    className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                  />
                </Field>
                <Field label="County">
                  <input
                    type="text"
                    value={form.county}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, county: e.target.value }))
                    }
                    className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                  />
                </Field>
                <Field label="Country">
                  <input
                    type="text"
                    value={form.country}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, country: e.target.value }))
                    }
                    className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                  />
                </Field>
                <Field label="Postcode">
                  <input
                    type="text"
                    value={form.postcode}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, postcode: e.target.value }))
                    }
                    className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                  />
                </Field>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg text-white bg-pink-600 hover:bg-pink-700 disabled:opacity-50 transition flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Settings Modal ──
const SettingsModal = ({ data, eventId, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    payment_type: data?.payment_type || "",
    email: data?.email || "",
    phone: data?.phone || "",
    iban: data?.iban || "",
    swift: data?.swift || "",
  });

  const handleSubmit = async () => {
    try {
      setSaving(true);
      const payload = {
        payment_type: form.payment_type,
        email: form.email,
        phone: form.phone,
        iban: form.iban,
        swift: form.swift,
      };
      const updated = await updateSettlementSetting(eventId, payload);
      onSave(updated);
      onClose();
    } catch (err) {
      console.error("Failed to update settings:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-md bg-slate-900 border border-white/10 rounded-xl shadow-xl">
        <div className="px-6 py-4 border-b border-white/10">
          <h2 className="text-white font-semibold text-sm">Edit Settings</h2>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs text-slate-400 mb-2">
              Type of Payments
            </label>
            <select
              value={form.payment_type || "-"}
              onChange={(e) => {
                setForm((p) => ({ ...p, payment_type: e.target.value }));
              }}
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            >
              <option value="">Select</option>
              <option value="manual">Manual</option>
              <option value="automatic_weekly">Automatic Weekly</option>
            </select>
          </div>
          <Field label="Email Address">
            <input
              type="email"
              value={form.email}
              onChange={(e) =>
                setForm((p) => ({ ...p, email: e.target.value }))
              }
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            />
          </Field>
          <Field label="Phone Number">
            <input
              type="text"
              value={form.phone}
              onChange={(e) =>
                setForm((p) => ({ ...p, phone: e.target.value }))
              }
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            />
          </Field>
          <Field label="IBAN">
            <input
              type="text"
              value={form.iban}
              onChange={(e) => setForm((p) => ({ ...p, iban: e.target.value }))}
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            />
          </Field>
          <Field label="Swift / BIC">
            <input
              type="text"
              value={form.swift}
              onChange={(e) =>
                setForm((p) => ({ ...p, swift: e.target.value }))
              }
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            />
          </Field>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg text-white bg-pink-600 hover:bg-pink-700 disabled:opacity-50 transition flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const MarkPaidModal = ({ settlement, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [error, setError] = useState(null);

  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPdfFile(file);
    setPdfPreview(file.name);
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError(null);

      const formData = new FormData();
      if (pdfFile) {
        formData.append("pop_file", pdfFile);
      }

      const updated = await markPaidSettlement(settlement.id, formData);
      onSave(updated);
      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.detail || err?.detail || "Failed to mark as paid.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-sm bg-slate-900 border border-white/10 rounded-xl shadow-xl">
        <div className="px-6 py-4 border-b border-white/10">
          <h2 className="text-white font-semibold text-sm">Mark as Paid</h2>
          <p className="text-xs text-slate-500 mt-0.5 font-mono">
            {settlement.settlement_id}
          </p>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* PDF upload */}
          <div className="space-y-1.5">
            <label className="block text-xs text-slate-400">
              Proof of Payment (PDF)
            </label>
            <div
              onClick={() => fileInputRef.current?.click()}
              className={`w-full border-2 border-dashed rounded-lg px-4 py-4 cursor-pointer transition text-center
                ${
                  pdfFile
                    ? "border-pink-500/30 bg-pink-500/5"
                    : "border-white/10 hover:border-pink-500/30 hover:bg-pink-500/5"
                }`}
            >
              {pdfFile ? (
                <div className="flex items-center justify-center gap-2">
                  <DocumentTextIcon className="w-4 h-4 text-pink-400" />
                  <span className="text-sm text-pink-400 truncate max-w-[200px]">
                    {pdfPreview}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPdfFile(null);
                      setPdfPreview(null);
                      fileInputRef.current.value = "";
                    }}
                    className="text-slate-500 hover:text-white transition ml-1"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1">
                  <DocumentTextIcon className="w-6 h-6 text-slate-500" />
                  <span className="text-xs text-slate-500">
                    Click to upload PDF
                  </span>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          {error && (
            <div className="px-3 py-2.5 bg-red-500/10 border border-red-500/20 rounded-lg text-xs text-red-400">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg text-white bg-pink-600 hover:bg-pink-700 disabled:opacity-50 transition flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              "Mark as Paid"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Format address helper ──
const formatAddress = (address) => {
  if (!address) return "—";
  const parts = [
    address.address_1,
    address.address_2,
    address.address_3,
    address.city,
    address.county,
    address.country,
    address.postcode,
  ].filter((p) => p && p.trim() !== "");
  return parts.length ? parts.join(", ") : "—";
};

// ── Main Page ──
const SettlementsPage = ({ eventId }) => {
  const [settlements, setSettlements] = useState([]);
  const [figures, setFigures] = useState(null);
  const [settings, setSettings] = useState(null);

  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [markPaidSettlement, setMarkPaidSettlement] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const handleSaveSettlement = (updated) => {
    setSettlements((prev) =>
      prev.map((s) => (s.id === updated.id ? { ...s, ...updated } : s)),
    );
    setToast({ type: "success", message: "Settlement updated." });
  };

  const handleDeleteSettlement = async () => {
    try {
      await cancelSettlement(deleteId);
      setDeleteId(null);
      setToast({ type: "success", message: "Settlement cancelled." });
      fetchSettlements();
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail ||
          err?.detail ||
          "Failed to cancel settlement.",
      });
      setDeleteId(null);
    }
  };

  const handleCrateManualSettlement = async () => {
    try {
      await createManualSettlement(eventId);
      setToast({ type: "success", message: "Settlement Created." });
      fetchSettlements();
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail ||
          err?.detail ||
          "Failed to cancel settlement.",
      });
    }
  };

  const fetchSettlements = async () => {
    try {
      setLoading(true);
      const data = await getSettlementsByEvent(eventId);
      setFigures(data.figures);
      setSettings(data.setting);
      setSettlements(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettlements();
  }, [page]);

  if (loading) return <PageLoader />;

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div>
        <h1 className="text-2xl font-semibold text-white">Settlements</h1>
        <p className="text-sm text-slate-400">
          Manage settlement figures and payment details
        </p>
      </div>

      {/* ── Top section ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Settlement Figures — read only */}
        <div className="bg-slate-900/60 border border-white/10 rounded-xl p-5 space-y-4">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">
            Settlement Figures
          </p>
          <div className="space-y-3">
            <div className="flex flex-col gap-0.5">
              <span className="text-xs text-slate-500">Total Sales</span>
              <span className="text-lg font-semibold text-white">
                {formatCurrency(figures?.total_sales, figures?.currency)}
              </span>
            </div>
            <div className="border-t border-white/5" />
            <div>
              <div className="flex flex-col">
                <span className="text-xs text-slate-500">Settled</span>
                <span className="text-sm font-medium text-emerald-400">
                  {formatCurrency(figures?.settled, figures?.currency)}
                </span>
              </div>
              <div className="flex flex-col mt-4">
                <span className="text-xs text-slate-500">Ready to Settle</span>
                <div className="relative group inline-block">
                  <span className="text-sm font-medium text-amber-400 cursor-help">
                    {formatCurrency(
                      figures?.ready_to_settle,
                      figures?.currency,
                    )}
                  </span>
                  {figures?.ready_tooltip && (
                    <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-slate-800 border border-white/10 text-xs text-slate-300 rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition pointer-events-none text-center">
                      {figures.ready_tooltip}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
                    </div>
                  )}
                </div>
                <div className="mt-1">
                  <button
                    onClick={() => {handleCrateManualSettlement()}}
                    className="flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white px-3 py-1 rounded-sm text-sm font-medium transition"
                  >
                    Settle Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Company Info — view with edit button */}
        <div className="bg-slate-900/60 border border-white/10 rounded-xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">
              Company Info
            </p>
            <button
              onClick={() => setShowCompanyModal(true)}
              className="p-1.5 rounded-md text-slate-500 hover:text-white hover:bg-slate-700 transition"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
          </div>
          <InfoRow label="Company Name" value={settings?.company_name} />
          <InfoRow label="VAT No" value={settings?.vat_no} />
          <InfoRow
            label="Representative"
            value={settings?.representative_name}
          />
          <InfoRow label="Address" value={formatAddress(settings?.address)} />
        </div>

        {/* Settings — view with edit button */}
        <div className="bg-slate-900/60 border border-white/10 rounded-xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">
              Settings
            </p>
            <button
              onClick={() => setShowSettingsModal(true)}
              className="p-1.5 rounded-md text-slate-500 hover:text-white hover:bg-slate-700 transition"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
          </div>
          <InfoRow label="Type of Payments" value={settings?.payment_type} />
          <InfoRow label="Email Address" value={settings?.email} />
          <InfoRow label="Phone Number" value={settings?.phone} />
          <InfoRow label="IBAN" value={settings?.iban} />
          <InfoRow label="Swift / BIC" value={settings?.swift} />
        </div>
      </div>

      {/* ── Settlements Table ── */}
      <div className="bg-slate-900/60 border border-white/10 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-white/10">
          <h2 className="text-sm font-semibold text-white">
            Settlement History
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table
            className="w-full border-collapse"
            style={{ minWidth: "700px" }}
          >
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-slate-400">
                <th className="px-6 py-3 text-left">Settlement ID</th>
                <th className="px-6 py-3 text-left">Settlement Date</th>
                <th className="px-6 py-3 text-center">Settlement Amount</th>
                <th className="px-6 py-3 text-center">Orders Report</th>
                <th className="px-6 py-3 text-center">Payment Status</th>
                <th className="px-6 py-3 text-center">POP PDF</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {settlements.map((s) => (
                <tr
                  key={s.id}
                  className="border-b border-white/5 last:border-none hover:bg-slate-800/40 transition"
                >
                  <td className="px-6 py-4 text-sm font-mono text-slate-300">
                    {s.settlement_id}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-300">
                    {s.settlement_date}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-white text-center">
                    {formatCurrency(s.amount, s.currency)}
                  </td>
                  <td className="px-6 py-4 text-center">
                    {s.orders_report_csv ? (
                      <button
                        onClick={() =>
                          window.open(s.orders_report_csv, "_blank")
                        }
                        className="p-1.5 rounded-md text-slate-400 hover:text-pink-400 hover:bg-pink-500/10 transition mx-auto flex items-center gap-1 text-xs"
                      >
                        <DocumentTextIcon className="w-4 h-4" />
                        CSV
                      </button>
                    ) : (
                      <span className="text-slate-600">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span
                      className={`px-2.5 py-0.5 rounded-md text-xs font-medium border ${statusStyle(s.payment_status)}`}
                    >
                      {statusLabel(s.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    {s.pop_file ? (
                      <button
                        onClick={() => window.open(s.pop_file, "_blank")}
                        className="p-1.5 rounded-md text-red-400 hover:text-red-300 hover:bg-red-500/10 transition mx-auto flex items-center gap-1 text-xs"
                      >
                        <DocumentTextIcon className="w-4 h-4" />
                        PDF
                      </button>
                    ) : (
                      <span className="text-slate-600">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => setMarkPaidSettlement(s)}
                        className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-700 transition"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteId(s.id)}
                        className="p-1.5 rounded-md text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {!settlements.length && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-10 text-center text-slate-400 text-sm"
                  >
                    No settlements found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Modals ── */}
      {showCompanyModal && (
        <CompanyInfoModal
          data={settings}
          eventId={eventId}
          onClose={() => setShowCompanyModal(false)}
          onSave={(updated) => {
            setSettings(updated);
            setToast({ type: "success", message: "Company info updated." });
          }}
        />
      )}

      {showSettingsModal && (
        <SettingsModal
          data={settings}
          eventId={eventId}
          onClose={() => setShowSettingsModal(false)}
          onSave={(updated) => {
            setSettings(updated);
            setToast({ type: "success", message: "Settings updated." });
          }}
        />
      )}

      {!!markPaidSettlement && (
        <MarkPaidModal
          settlement={markPaidSettlement}
          onClose={() => setMarkPaidSettlement(null)}
          onSave={handleSaveSettlement}
        />
      )}

      {/* ── Delete Confirmation ── */}
      {!!deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-sm bg-slate-900 border border-white/10 rounded-xl shadow-xl">
            <div className="px-6 py-5">
              <h2 className="text-white font-semibold text-sm mb-3">
                Cancel Settlement
              </h2>
              <p className="text-slate-400 text-sm">
                Are you sure you want to cancel this settlement? This action
                cannot be undone.
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
              <button
                onClick={() => setDeleteId(null)}
                className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
              >
                No
              </button>
              <button
                onClick={handleDeleteSettlement}
                className="px-4 py-2 text-sm rounded-lg text-white bg-red-600 hover:bg-red-700 transition"
              >
                Yes, Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer>
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}
      </ToastContainer>
    </div>
  );
};

export default SettlementsPage;
